import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Home, UserCircle2, UserPlus, Package, ChevronDown, ChevronRight, CheckCircle2, Check, AlertTriangle, X, Plus, Loader2, Calendar as CalIcon, Bookmark, Save, Trash2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import Avatar from "../components/Avatar";
import { confirmDialog } from "../lib/confirm";
import StatusHistoryTimeline from "../components/StatusHistoryTimeline";
import RequestDetailModal from "../components/RequestDetailModal";
import { STATUS_COLORS, withAlpha, BRAND_COLORS, taskStatusColor, requestColorKey } from "../lib/bookingStatus";
import { StatusBadge } from "../components/StatusBadge";
import { SeenDot, SeenFilterToggle, isUnseenAssignment } from "../components/SeenIndicator";
import { deDate, deDateShort, deDateShortTime } from "../lib/dateFormat";

const STATUSES = ["new", "received", "under_review", "quote_required", "awaiting_approval", "scheduled", "in_progress", "completed", "cancelled"];

const isDoneStatus = (s) => s === "completed" || s === "cancelled";

// Feb 2026 · User-Directive · Priorität „Dringend" muss visuell klar
// unterscheidbar sein vom roten Sidebar-Neu-Badge (Verwechslungsgefahr).
// Statt System-Rot bekommt „Dringend" ab jetzt einen Amber/Orange-Ton +
// AlertTriangle-Icon. Storniert (Rot gestreift diagonal) und der
// Sidebar-Neu-Zähler behalten Rot als Alleinstellungsmerkmal.
const URGENT_COLOR = "#D97706";     // Amber-600 — kräftiger warmer Ton
const URGENT_COLOR_DARK = "#B45309"; // Amber-700 — für Border/Text-Kontrast

// Feb 2026 · Systemweiter Status-Farbcode für die Anfragen-Cards.
// Truth-Source ist `taskStatusColor` aus bookingStatus.js — Grau (angefragt/
// neu/wartet), Blau (in Bearbeitung/geplant/geprüft), Grün (erledigt),
// Rot (dringend/überfällig). Für Storniert überschreiben wir das
// dortige Grau-Mapping mit einem diagonalen Rot-Streifen, damit
// die Anfragen-Seite deckungsgleich mit Buchungen + Kalender bleibt.
const REQ_STATUS_LABEL = {
  new: "Neu",
  received: "Eingegangen",
  under_review: "In Prüfung",
  quote_required: "Angebot benötigt",
  awaiting_approval: "Wartet auf Freigabe",
  scheduled: "Terminiert",
  in_progress: "In Bearbeitung",
  completed: "Erledigt",
  cancelled: "Storniert",
};
function requestStatusVisual(r) {
  const isCancelled = r?.status === "cancelled";
  const stripes = `repeating-linear-gradient(135deg, ${STATUS_COLORS.red} 0 4px, ${withAlpha(STATUS_COLORS.red, 0.35)} 4px 8px)`;
  if (isCancelled) {
    return {
      label: REQ_STATUS_LABEL.cancelled,
      stripeBg: stripes,
      pillBg: stripes,
      pillFg: BRAND_COLORS.ivory,
      isCancelled: true,
    };
  }
  const c = taskStatusColor(r?.status);
  return {
    label: REQ_STATUS_LABEL[r?.status] || r?.status || "—",
    stripeBg: c.bg,
    pillBg: c.bg,
    pillFg: c.fg,
    isCancelled: false,
  };
}

function RequestCard({ r, team, user, onSetStatus, onAssign, onOpenDetail, t, indent = false }) {
  const done = isDoneStatus(r.status);
  const urgent = !done && r.priority === "urgent";
  // Feb 2026 · User-Directive „Zuweisungen sind Admin-only":
  // Concierges sehen die aktuelle Zuweisung read-only und dürfen keine
  // Bearbeiter mehr verschieben oder Anfragen selbst übernehmen.
  const isAdmin = user?.role === "admin";
  // Feb 2026 · User-Directive · Systemweiter Farbcode auch für Anfragen-
  // Cards. Left-Stripe (4 px) trägt die Statusfarbe · Grau (Angefragt) ·
  // Blau (In Bearbeitung) · Grün (Erledigt) · Rot gestreift (Storniert).
  // Der frühere gold- oder rot-eingefärbte `border-l-4` (urgent-Priorität
  // bzw. Batch-Indent) wird durch diesen Stripe ersetzt — Priorität
  // bleibt als eigenständiges Corner-Badge + Puls-Animation erhalten,
  // damit sie orthogonal zum Status-Farbcode signalisiert.
  const visual = requestStatusVisual(r);
  return (
    <div
      className={`bg-white relative border-t border-r border-b border-navy/10 p-6 transition-opacity ${done ? "opacity-60" : ""} ${urgent ? "animate-pulse-slow" : ""}`}
      style={{
        ...(urgent && !done ? { background: withAlpha(URGENT_COLOR, 0.05) } : {}),
      }}
      data-testid={`team-request-${r.id}${urgent ? "-urgent" : ""}`}
      data-status={r.status || "new"}
    >
      {/* Systemweiter Status-Farbstreifen links (4 px). Bei Cancelled ein
          diagonaler Rot-Streifen, sonst solide Statusfarbe. */}
      <span
        aria-hidden="true"
        data-testid={`team-request-stripe-${r.id}`}
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ background: visual.stripeBg }}
      />
      {urgent && !done && (
        // Feb 2026 · User-Directive · „Dringend" ohne roten Kreis.
        // Amber-Label mit AlertTriangle-Icon — klar unterscheidbar vom
        // System-Rot (Neu-Sidebar-Badge · Cancelled-Streifen).
        <div
          className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-sm border"
          style={{ background: withAlpha(URGENT_COLOR, 0.12), color: URGENT_COLOR_DARK, borderColor: withAlpha(URGENT_COLOR, 0.55) }}
          data-testid={`team-request-urgent-badge-${r.id}`}
        >
          <AlertTriangle className="w-3 h-3" strokeWidth={2.2} />
          Dringend
        </div>
      )}
      {done && r.status === "completed" && (
        <div
          className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-full"
          style={{ background: STATUS_COLORS.green, color: BRAND_COLORS.ivory }}
          data-testid={`team-request-done-badge-${r.id}`}
        >
          <Check className="w-3 h-3" strokeWidth={3} /> Erledigt
        </div>
      )}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <SeenDot entity={r} viewerId={user?.id} testid={`team-request-seen-${r.id}`} />
            {/* Feb 2026 · Status-Pill in Systemfarbe — Grau/Blau/Grün/Rot
                gestreift. Steht an prominenter Stelle vor Priorität/Datum. */}
            <StatusBadge
              colorKey={requestColorKey(r)}
              label={visual.label}
              striped={r.status === "cancelled"}
              testid={`team-request-status-badge-${r.id}`}
            />
            <span className="text-navy/20">·</span>
            <span className="label-caps">{r.priority}</span>
            <span className="text-navy/20">·</span>
            <span className="label-caps">{deDate(r.created_at)}</span>
            {/* Feb 2026 · User-Directive · JEDE Anfragen-Zeile muss den
                Kundennamen zeigen. Für Batch-Kinder (indent=true) bleibt
                der Name ausgeblendet, weil er im Batch-Header steht. */}
            {!indent && (
              <>
                <span className="text-navy/20">·</span>
                <span
                  className="label-caps text-navy font-medium"
                  data-testid={`team-request-customer-${r.id}`}
                >
                  {r.customer_name || "Nicht zugeordnet"}
                </span>
              </>
            )}
            {/* 24 Jun 2026 · User-Directive · Zugehöriges Objekt direkt in
                der Listenansicht (nicht erst im Detail-Modal). Klickbar →
                springt zur Objekt-Detailseite (`/team/properties/{id}`).
                Wird nur gezeigt, wenn die Anfrage einem Objekt zugeordnet ist. */}
            {r.property_name && (
              <>
                <span className="text-navy/20">·</span>
                {r.property_id ? (
                  <Link
                    to={`/team/properties/${r.property_id}`}
                    className="inline-flex items-center gap-1 label-caps text-navy-dark/70 hover:text-gold-dark transition-colors"
                    data-testid={`team-request-property-${r.id}`}
                    title={`Zur Objekt-Detailseite: ${r.property_name}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Home className="w-3 h-3" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
                    {r.property_name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 label-caps text-navy-dark/70"
                    data-testid={`team-request-property-${r.id}`}
                    title="Zugehöriges Objekt"
                  >
                    <Home className="w-3 h-3" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
                    {r.property_name}
                  </span>
                )}
              </>
            )}
          </div>
          <h3
            className={`font-display text-xl text-navy mb-2 ${done ? "line-through decoration-navy-dark/25" : ""} ${onOpenDetail ? "cursor-pointer hover:text-gold-dark transition-colors" : ""}`}
            onClick={onOpenDetail ? () => onOpenDetail(r) : undefined}
            data-testid={`team-request-open-detail-${r.id}`}
            role={onOpenDetail ? "button" : undefined}
            tabIndex={onOpenDetail ? 0 : undefined}
            onKeyDown={(e) => {
              if (onOpenDetail && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onOpenDetail(r);
              }
            }}
            title={onOpenDetail ? "Details öffnen" : undefined}
          >
            {r.title}
          </h3>
          <p
            className={`text-[13px] text-navy-dark/70 leading-relaxed whitespace-pre-wrap ${onOpenDetail ? "cursor-pointer" : ""}`}
            onClick={onOpenDetail ? () => onOpenDetail(r) : undefined}
          >
            {r.description}
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            {r.assignee ? (
              <>
                <div className="flex items-center gap-2 border border-navy/10 bg-ivory/60 px-3 py-1.5" data-testid={`team-assignee-${r.id}`}>
                  <Avatar name={r.assignee.name} fileId={r.assignee.avatar_file_id} size={28} />
                  <div className="text-[12px] text-navy">
                    <span className="text-navy-dark/60">Bearbeitet von </span>
                    <span className="font-medium">{r.assignee.name}</span>
                  </div>
                </div>
                {isAdmin && (
                  <button onClick={() => onAssign(r.id, null)}
                    className="text-[11px] tracking-wider uppercase text-navy-dark/60 hover:text-navy underline decoration-dotted"
                    data-testid={`team-unassign-${r.id}`}>
                    Zuweisung entfernen
                  </button>
                )}
              </>
            ) : isAdmin ? (
              <button onClick={() => onAssign(r.id, user?.id)}
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-navy border border-navy/25 hover:border-gold px-3 py-2"
                data-testid={`team-take-request-${r.id}`}>
                <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> Ich übernehme
              </button>
            ) : (
              // Feb 2026 · Concierge-Read-Only-Anzeige: „Noch nicht zugewiesen"
              // ohne Interaktions-Möglichkeit.  Zuweisungen sind Admin-only.
              <div
                className="flex items-center gap-2 text-[12px] text-navy-dark/60 italic"
                data-testid={`team-assignee-readonly-empty-${r.id}`}
              >
                <UserCircle2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                Noch nicht zugewiesen
              </div>
            )}
            {isAdmin && (
              <select value={r.assigned_to || ""} onChange={(e) => onAssign(r.id, e.target.value || null)}
                data-testid={`team-assign-select-${r.id}`}
                className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[12px] text-navy-dark">
                <option value="">— zuweisen an —</option>
                {team.map((m) => (<option key={m.id} value={m.id}>{m.name || m.email} · {m.role}</option>))}
              </select>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <select data-testid={`team-request-status-${r.id}`}
            value={r.status} onChange={(e) => onSetStatus(r.id, e.target.value)}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-[13px]">
            {STATUSES.map((s) => <option key={s} value={s}>{t.requests.status[s] || s}</option>)}
          </select>
          {r.assigned_at && (
            <div className="text-[10.5px] text-navy-dark/50 flex items-center gap-1">
              <UserCircle2 className="w-3 h-3" />
              seit {deDateShort(r.assigned_at)}
            </div>
          )}
        </div>
      </div>
      <StatusHistoryTimeline
        history={r.status_history}
        currentStatus={r.status}
        testid={`team-request-status-history-${r.id}`}
      />
    </div>
  );
}

export default function TeamRequests() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [team, setTeam] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [collapsed, setCollapsed] = useState({}); // { batch_id: true } — visually collapsed
  // Feb 2026 · Add-Task Umzug — vom Team-Kunden-Detail hierher. Team-erfasste
  // Aufgaben (`source=team_captured`) haben genau EINEN klaren Anlege-Ort.
  const [addOpen, setAddOpen] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newPriority, setNewPriority] = useState("normal");
  // Feb 2026 · User-Directive „Aufgabe mit Datum im Kalender": Ausführungs-
  // datum optional erfassen. Sobald gesetzt, taucht die Aufgabe automatisch
  // im Kalender am entsprechenden Tag auf (systemweiter Farbcode via status).
  const [newDate, setNewDate] = useState("");
  // Feb 2026 · User-Directive „Zeitfenster statt Datumsspitze": optionales
  // Ende-Datum. Gesetzt → Aufgabe rendert als Balken über den Zeitraum.
  const [newDateTo, setNewDateTo] = useState("");
  const [adding, setAdding] = useState(false);
  const canCreate = user?.role === "admin" || user?.role === "concierge";
  // Feb 2026 · User-Directive · Vollständige Detailansicht per Klick auf
  // Titel/Beschreibung einer Anfrage. Modal zeigt alle Kunden-Angaben
  // (Beschreibung, Wunschdatum/-zeit, Budget, Anhänge, Freigabe-Flag,
  // Objekt) — siehe `components/RequestDetailModal.jsx`.
  const [openDetail, setOpenDetail] = useState(null);
  // Feb 2026 · URL-driven „Nur dringend"-Filter (pre-existing but never
  // destrukturiert — Bug lag dormant, weil `urgentOnly` als undefined
  // always-falsy interpretiert wurde. Jetzt sauber deklariert.)
  const [searchParams, setSearchParams] = useSearchParams();
  const urgentOnly = searchParams.get("filter") === "urgent";
  // Feb 2026 · User-Directive · Anfragen zeigt standardmäßig NUR OFFENE
  // (nicht completed, nicht cancelled). Erledigte Anfragen bleiben über
  // den Toggle „Auch erledigte" einsehbar, damit der Audit-Trail erhalten
  // bleibt. Batches gelten als erledigt, sobald ALLE Positionen erledigt/
  // storniert sind — dann verschwinden sie standardmäßig ebenfalls.
  const includeDone = searchParams.get("done") === "1";
  // Feb 2026 · User-Directive „Chip-Filter Nur an Buchung geknüpft":
  // Ein-Klick-Filter, mit dem der Concierge nur noch die Anfragen sieht,
  // die einer konkreten Abwesenheit angehängt sind (booking_id gesetzt).
  // Damit sieht man ohne Suche, welche Zusatzleistungen für welche
  // Anreise noch offen sind. Persistiert über `?linked=1` in der URL,
  // damit Deep-Links (z.B. aus dem PIN-Audit-Log oder aus Nachrichten)
  // die Ansicht direkt öffnen können.
  const bookingLinkedOnly = searchParams.get("linked") === "1";
  // 24 Jun 2026 · User-Directive · Filter nach Objekt (property_id) — zeigt
  // schnell alle Wünsche für z. B. Villa Pfaff. Persistiert über `?property=`.
  const propertyFilter = searchParams.get("property") || "";
  const unseenOnly = searchParams.get("unseen") === "1";
  const showDone = () => {
    const next = new URLSearchParams(searchParams);
    next.set("done", "1");
    setSearchParams(next, { replace: true });
  };
  const hideDone = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("done");
    setSearchParams(next, { replace: true });
  };
  const enableBookingLinkedOnly = () => {
    const next = new URLSearchParams(searchParams);
    next.set("linked", "1");
    setSearchParams(next, { replace: true });
  };
  const disableBookingLinkedOnly = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("linked");
    setSearchParams(next, { replace: true });
  };
  const setPropertyFilter = (pid) => {
    const next = new URLSearchParams(searchParams);
    if (pid) next.set("property", pid);
    else next.delete("property");
    setSearchParams(next, { replace: true });
  };
  // Feb 2026 · Concierge-Impersonation: wenn Admin eine Concierge-
  // Perspektive gewählt hat (oder ein echter Concierge eingeloggt ist),
  // wird die Liste strikt auf `assigned_to === activeConciergeId`
  // gefiltert. Details siehe `lib/auth.js::activeConciergeId`.
  const { activeConciergeId, activeConciergeName, isImpersonatingConcierge, impersonatedConcierge } = useAuth();
  const unseenViewer = activeConciergeId || (user?.role === "concierge" ? user?.id : null);

  const load = useCallback(async () => {
    const [r, m, c] = await Promise.all([
      api.get("/requests"),
      api.get("/team/members"),
      canCreate ? api.get("/team/customers") : Promise.resolve({ data: [] }),
    ]);
    setRequests(r.data);
    setTeam(m.data);
    setCustomers((c.data || []).filter((u) => (u.role === "customer" || u.role === "family")));
  }, [canCreate]);
  useEffect(() => { load(); }, [load]);

  // Feb 2026 · 9 Sep 2026 · User-Directive „Saved Filters":
  // Concierges speichern ihre Lieblings-Filter-Kombinationen als benannte
  // Ansicht (Backend: `saved_filters.py`, Scope `team_requests`). Filter-
  // Payload umfasst die drei Chip-States (`urgent`, `done`, `linked`) —
  // damit lassen sich Kontexte wie „Morgen brennt es" oder
  // „Winterservice-Übersicht" per Klick wiederherstellen.
  const [savedViews, setSavedViews] = useState([]);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");
  const [savingView, setSavingView] = useState(false);

  const loadViews = async () => {
    try {
      const { data } = await api.get("/me/saved-filters", { params: { scope: "team_requests" } });
      setSavedViews(data || []);
    } catch (err) {
      // 403/500 → still faelen; das Feature ist optional.
      console.debug("saved-filters load failed:", err?.response?.status);
    }
  };
  useEffect(() => { if (canCreate) loadViews(); }, [canCreate]);

  const currentFilterPayload = () => ({
    urgent: !!urgentOnly,
    done: !!includeDone,
    linked: !!bookingLinkedOnly,
  });

  const applyView = (v) => {
    const f = v?.filters || {};
    const next = new URLSearchParams();
    if (f.urgent) next.set("filter", "urgent");
    if (f.done) next.set("done", "1");
    if (f.linked) next.set("linked", "1");
    setSearchParams(next, { replace: true });
    toast.success(`Ansicht „${v.name}" geladen.`);
  };

  const saveCurrentView = async () => {
    const name = saveViewName.trim();
    if (!name) { toast.error("Bitte einen Namen eingeben."); return; }
    setSavingView(true);
    try {
      const { data } = await api.post("/me/saved-filters", {
        scope: "team_requests",
        name,
        filters: currentFilterPayload(),
      });
      setSavedViews((cur) => {
        const filtered = cur.filter((v) => v.id !== data.id && v.name !== data.name);
        return [...filtered, data].sort((a, b) => a.name.localeCompare(b.name));
      });
      setSaveViewOpen(false);
      setSaveViewName("");
      toast.success(`Ansicht „${data.name}" gespeichert.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSavingView(false); }
  };

  const deleteView = async (v) => {
    if (!(await confirmDialog({
      title: `Ansicht „${v.name}" löschen?`,
      description: "Die Filter-Kombination wird aus Ihrer persönlichen Liste entfernt.",
      confirmText: "Löschen",
      variant: "danger",
    }))) return;
    try {
      await api.delete(`/me/saved-filters/${v.id}`);
      setSavedViews((cur) => cur.filter((x) => x.id !== v.id));
      toast.success(`Ansicht „${v.name}" entfernt.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  const resetAddForm = () => {
    setNewCustomerId("");
    setNewLabel("");
    setNewDesc("");
    setNewPriority("normal");
    setNewDate("");
    setNewDateTo("");
  };
  const addTask = async (e) => {
    e?.preventDefault();
    const label = newLabel.trim();
    if (!label || !newCustomerId) return;
    setAdding(true);
    try {
      await api.post(`/team/customers/${newCustomerId}/tasks`, {
        label,
        description: newDesc.trim() || undefined,
        priority: newPriority,
        // Feb 2026 · Bei gesetztem Datum landet die Aufgabe im Kalender am
        // entsprechenden Tag (systemweiter Farbcode via `status`).
        date_needed: newDate || undefined,
        // Feb 2026 · Bei zusätzlichem Ende-Datum spannt die Aufgabe einen
        // Balken über den Zeitraum (Renovation, Möbel-Anlieferung …).
        date_to: newDateTo || undefined,
      });
      toast.success(
        newDate && newDateTo && newDateTo > newDate
          ? `Aufgabe angelegt · Balken vom ${newDate} bis ${newDateTo} im Kalender.`
          : newDate
            ? `Aufgabe angelegt · erscheint am ${newDate} im Kalender.`
            : "Aufgabe angelegt · sichtbar in Anfragen & im Kalender."
      );
      resetAddForm();
      setAddOpen(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setAdding(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/requests/${id}/status`, null, { params: { status } });
      toast.success("Status aktualisiert.");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };
  const assign = async (id, assignee_id) => {
    try {
      await api.patch(`/requests/${id}/assign`, { assigned_to: assignee_id || null });
      toast.success(assignee_id ? "Anfrage übernommen." : "Zuweisung entfernt.");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  const assignBatch = async (batch_id, assignee_id) => {
    try {
      const { data } = await api.patch(`/requests/batch/${batch_id}/assign`, { assigned_to: assignee_id || null });
      toast.success(`${data.updated} Position${data.updated === 1 ? "" : "en"} übernommen.`);
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  const completeBatch = async (batch_id, openCount) => {
    if (openCount === 0) return;
    if (!(await confirmDialog({
      title: "Sammel-Bestellung erledigen?",
      description: `Alle ${openCount} offenen Positionen werden als erledigt markiert.`,
      confirmText: "Erledigen",
    }))) return;
    try {
      const { data } = await api.patch(`/requests/batch/${batch_id}/complete`);
      toast.success(`${data.updated} Position${data.updated === 1 ? "" : "en"} als erledigt markiert.`);
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  // Group requests: entries are either single-request objects (kind='single')
  // or batch objects (kind='batch') containing multiple related requests.
  const items = useMemo(() => {
    const byBatch = {};
    const singles = [];
    const isOpen = (r) => !["completed", "cancelled"].includes(r.status);
    let filteredSource = urgentOnly
      ? requests.filter((r) => r.priority === "urgent" && isOpen(r))
      : requests;
    // Feb 2026 · Directive · Nur offene Anfragen anzeigen (Standard) —
    // erledigte/stornierte Singles werden ausgeblendet, sofern der
    // „done"-Query-Param nicht gesetzt ist. Bei Batches wird die Sichtbar-
    // keit weiter unten auf Batch-Ebene entschieden (openCount === 0).
    if (!includeDone && !urgentOnly) {
      filteredSource = filteredSource.filter((r) => r.batch_id || isOpen(r));
    }
    // Feb 2026 · Concierge-Filter · User-Directive „Buchung == ein
    // Concierge" (1 Sep 2026): Backend scoped für echte Concierges bereits
    // strikt auf `assigned_to == user.id`. Der zusätzliche Client-Filter
    // greift NUR bei Admin-Impersonation (dann kennt das Backend die
    // impersonierte Zielrolle nicht).
    //
    // Feb 2026 · 16 Sep 2026 · User-Directive „Admin-Impersonation zeigt
    // nur zugewiesene Daten": Filter erweitert um Objekt-Haupt-Concierge-
    // Fallback (`assigned_concierge_id`). Zusätzlich robust gegen
    // Einstiegs-Pfade, die nur `impersonatedConcierge` setzen (ohne
    // `effectiveRole` auf concierge zu drehen): wir nehmen das direkte
    // Signal als Fallback.
    const _scopeReqId = isImpersonatingConcierge
      ? (activeConciergeId || impersonatedConcierge?.id || null)
      : null;
    if (_scopeReqId) {
      filteredSource = filteredSource.filter((r) =>
        r.assigned_to === _scopeReqId
        || r.assigned_concierge_id === _scopeReqId
      );
    }
    // Feb 2026 · Filter „Nur an Buchung geknüpft" — booking_id muss gesetzt
    // sein. Wichtig: der Filter wirkt AUCH auf Batches (die typischer-
    // weise komplett aus extras_bulk mit booking_id bestehen), damit die
    // Zählung 1:1 mit dem Ergebnis stimmt.
    if (bookingLinkedOnly) {
      filteredSource = filteredSource.filter((r) => !!r.booking_id);
    }
    // 24 Jun 2026 · Objekt-Filter — nur Anfragen des gewählten Objekts
    // (property_id). Greift vor der Batch-Gruppierung, damit die Zählung
    // 1:1 mit dem Ergebnis stimmt (leere Batches fallen automatisch weg).
    if (propertyFilter) {
      filteredSource = filteredSource.filter((r) => r.property_id === propertyFilter);
    }
    if (unseenOnly) {
      filteredSource = filteredSource.filter((r) => isUnseenAssignment(r, unseenViewer, "assigned_to"));
    }
    for (const r of filteredSource) {
      if (r.batch_id) {
        (byBatch[r.batch_id] = byBatch[r.batch_id] || []).push(r);
      } else {
        singles.push({ kind: "single", request: r, sortKey: r.created_at });
      }
    }
    let batches = Object.entries(byBatch).map(([batch_id, list]) => {
      const sorted = list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      const openCount = sorted.filter(isOpen).length;
      return {
        kind: "batch",
        batch_id,
        requests: sorted,
        openCount,
        customer_name: sorted[0].customer_name || "",
        first_at: sorted[0].created_at,
        sortKey: sorted[sorted.length - 1].created_at || sorted[0].created_at,
      };
    });
    // Standardansicht: Batches ohne offene Positionen ausblenden.
    if (!includeDone && !urgentOnly) {
      batches = batches.filter((b) => b.openCount > 0);
    }
    return [...singles, ...batches].sort((a, b) => (b.sortKey || "").localeCompare(a.sortKey || ""));
  }, [requests, urgentOnly, includeDone, bookingLinkedOnly, propertyFilter, unseenOnly, unseenViewer, activeConciergeId, isImpersonatingConcierge, impersonatedConcierge?.id]);

  const doneCount = useMemo(
    () => requests.filter((r) => ["completed", "cancelled"].includes(r.status)).length,
    [requests],
  );
  const unseenCount = useMemo(
    () => requests.filter((r) => !["completed", "cancelled"].includes(r.status) && isUnseenAssignment(r, unseenViewer, "assigned_to")).length,
    [requests, unseenViewer],
  );
  const toggleUnseen = () => {
    const next = new URLSearchParams(searchParams);
    if (unseenOnly) next.delete("unseen"); else next.set("unseen", "1");
    setSearchParams(next, { replace: true });
  };

  // 24 Jun 2026 · Objekt-Optionen aus den geladenen Anfragen ableiten —
  // nur Objekte, die tatsächlich Wünsche haben, erscheinen im Filter.
  const propertyOptions = useMemo(() => {
    const map = new Map();
    for (const r of requests) {
      if (r.property_id && r.property_name) map.set(r.property_id, r.property_name);
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [requests]);
  const propertyFilterName = propertyOptions.find((o) => o.id === propertyFilter)?.name || "";

  // Feb 2026 · Anzahl offener, an eine Buchung geknüpfter Anfragen —
  // Zähler auf dem Filter-Chip. Zeigt sich nur, wenn > 0, damit die
  // Filter-Zeile bei leerem Set nicht mit einem toten Chip zuwuchert.
  const bookingLinkedOpenCount = useMemo(
    () => requests.filter((r) => !!r.booking_id && !["completed", "cancelled"].includes(r.status)).length,
    [requests],
  );

  const urgentOpenCount = useMemo(
    () => requests.filter((r) => r.priority === "urgent" && !["completed", "cancelled"].includes(r.status)).length,
    [requests],
  );
  // Feb 2026 · Kombinierbare Filter — Schnittmengen-Zähler für den Fall,
  // dass „Nur dringend" und „Nur an Buchung geknüpft" gleichzeitig aktiv
  // sind. So sieht der Concierge sofort, wo es für die nächste Anreise
  // wirklich brennt (Priorität dringend UND an eine konkrete Abwesenheit
  // gekoppelt).
  const urgentAndLinkedOpenCount = useMemo(
    () => requests.filter(
      (r) => r.priority === "urgent"
        && !!r.booking_id
        && !["completed", "cancelled"].includes(r.status),
    ).length,
    [requests],
  );
  const clearFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };
  const enableFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.set("filter", "urgent");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-requests">
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-3">Weissblom Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">{t.team.requests_title}</h1>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setAddOpen((o) => !o)}
            data-testid="team-requests-add-task-btn"
            className="inline-flex items-center gap-1.5 border border-navy hover:bg-navy hover:text-ivory px-4 py-2 text-[11px] tracking-[0.14em] uppercase text-navy transition-colors"
            style={addOpen ? { background: BRAND_COLORS.navy, color: BRAND_COLORS.ivory } : {}}
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
            Aufgabe hinzufügen
          </button>
        )}
      </div>

      {/* Team-Add-Task Inline-Formular (Feb 2026 · Umzug vom Kunden-Detail) */}
      {canCreate && addOpen && (
        <form
          onSubmit={addTask}
          className="border border-gold/40 bg-ivory/60 p-5 md:p-6 mb-8"
          data-testid="team-requests-add-task-form"
        >
          <div className="flex items-start gap-2 mb-4">
            <UserCircle2 className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.6} style={{ color: BRAND_COLORS.gold }} />
            <div className="text-[12px] text-navy-dark/70 leading-relaxed">
              Neue vom Team erfasste Aufgabe (z. B. Fensterputzer, Bootsservice, Saisonarbeit). Wird als reguläre Anfrage angelegt und im Kunden-Kalender + der Kunden-App sichtbar.
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="label-caps block mb-1.5">Kunde</label>
              <select
                value={newCustomerId}
                onChange={(e) => setNewCustomerId(e.target.value)}
                required
                disabled={adding}
                data-testid="team-requests-add-task-customer"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-navy-dark text-[13px]"
              >
                <option value="">Bitte wählen …</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Priorität</label>
              <select
                value={newPriority}
                onChange={(e) => setNewPriority(e.target.value)}
                disabled={adding}
                data-testid="team-requests-add-task-priority"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-navy-dark text-[13px]"
              >
                <option value="normal">Normal</option>
                <option value="high">Hoch</option>
                <option value="urgent">Dringend</option>
              </select>
            </div>
          </div>
          {/* Feb 2026 · User-Directive · Datumsfeld. Sobald ein Datum
              eingegeben wird, erscheint die Aufgabe im Kalender am
              entsprechenden Tag mit systemweitem Farbcode
              (Grau/Blau/Grün/Rot gestreift, gesteuert via Status).
              Feb 2026 · Optionales Ende-Datum → mehrtägige Aufgaben
              rendern als Balken über den Zeitraum. */}
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="label-caps block mb-1.5">
                Datum · Start <span className="text-navy-dark/45 normal-case tracking-normal">— optional</span>
              </label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                disabled={adding}
                data-testid="team-requests-add-task-date"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-navy-dark text-[13px]"
              />
            </div>
            <div>
              <label className="label-caps block mb-1.5">
                Datum · Ende <span className="text-navy-dark/45 normal-case tracking-normal">— optional · für Balken</span>
              </label>
              <input
                type="date"
                value={newDateTo}
                min={newDate || undefined}
                onChange={(e) => setNewDateTo(e.target.value)}
                disabled={adding || !newDate}
                data-testid="team-requests-add-task-date-to"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-navy-dark text-[13px] disabled:opacity-40"
              />
              {!newDate && (
                <p className="text-[10.5px] text-navy-dark/45 mt-1 leading-snug">
                  Erst Start-Datum wählen, dann optional ein Ende für den Balken.
                </p>
              )}
            </div>
          </div>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Titel · z. B. „Skiservice für vier Paar Ski"
            required
            disabled={adding}
            data-testid="team-requests-add-task-title"
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark text-[13px] mb-2"
          />
          <textarea
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            rows={2}
            placeholder="Details · Optional (z. B. Timing, Ort, Kontakt)"
            disabled={adding}
            data-testid="team-requests-add-task-desc"
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark text-[13px] mb-3 leading-relaxed"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setAddOpen(false); resetAddForm(); }}
              disabled={adding}
              data-testid="team-requests-add-task-cancel"
              className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-navy px-3 py-2"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={adding || !newLabel.trim() || !newCustomerId}
              data-testid="team-requests-add-task-submit"
              className="inline-flex items-center gap-1.5 text-ivory px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
            >
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2} />}
              Aufgabe anlegen
            </button>
          </div>
        </form>
      )}

      {/* Feb 2026 · Filter-Chips — „Nur dringend" + „Auch erledigte" +
          „Nur an Buchung geknüpft". Standardansicht: nur offene Anfragen.
          Erledigt/storniert lassen sich per Chip einblenden (Audit-Trail
          bleibt erreichbar). „Nur an Buchung geknüpft" hilft dem Concierge,
          mit einem Klick zu sehen, welche Zusatzleistungen für welche
          Anreise noch offen sind. */}
      {(urgentOnly || urgentOpenCount > 0 || doneCount > 0 || includeDone || bookingLinkedOnly || bookingLinkedOpenCount > 0 || savedViews.length > 0 || propertyOptions.length > 0 || propertyFilter || unseenOnly || unseenCount > 0) && (
        <div className="mb-5 flex items-center gap-3 flex-wrap" data-testid="team-requests-filter-bar">
          {/* 24 Jun 2026 · Objekt-Filter — Dropdown mit allen Objekten, die
              offene/erledigte Wünsche haben. „Alle Objekte" setzt zurück. */}
          {(propertyOptions.length > 0 || propertyFilter) && (
            <div className="inline-flex items-center gap-1.5" data-testid="team-requests-property-filter">
              <Home className="w-3.5 h-3.5" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
              <select
                value={propertyFilter}
                onChange={(e) => setPropertyFilter(e.target.value)}
                data-testid="team-requests-property-select"
                aria-label="Nach Objekt filtern"
                className="bg-white border border-navy/25 focus:border-gold focus:outline-none pl-3 pr-8 py-1.5 text-[12px] text-navy-dark rounded-full"
              >
                <option value="">Alle Objekte</option>
                {propertyOptions.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              {propertyFilter && (
                <button
                  type="button"
                  onClick={() => setPropertyFilter("")}
                  data-testid="team-requests-property-clear"
                  title="Objekt-Filter zurücksetzen"
                  className="text-navy-dark/50 hover:text-navy p-1"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              )}
            </div>
          )}
          {urgentOnly ? (
            <button
              type="button"
              onClick={clearFilter}
              data-testid="team-requests-filter-active"
              className="inline-flex items-center gap-2 text-ivory px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full hover:opacity-90 transition-opacity"
              style={{ background: URGENT_COLOR_DARK }}
              aria-label="Filter zurücksetzen — alle Anfragen anzeigen"
            >
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
              Nur dringend · {urgentOpenCount}
              <X className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
            </button>
          ) : urgentOpenCount > 0 ? (
            <button
              type="button"
              onClick={enableFilter}
              data-testid="team-requests-filter-inactive"
              className="inline-flex items-center gap-2 text-navy px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full border hover:opacity-90 transition-colors"
              style={{ borderColor: withAlpha(URGENT_COLOR, 0.55), color: URGENT_COLOR_DARK }}
              aria-label={`Nur dringende Anfragen anzeigen (${urgentOpenCount})`}
            >
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} style={{ color: URGENT_COLOR_DARK }} />
              Nur dringend ({urgentOpenCount})
            </button>
          ) : null}
          <SeenFilterToggle active={unseenOnly} count={unseenCount} onToggle={toggleUnseen} testidBase="team-requests-unseen" />
          {!urgentOnly && doneCount > 0 && (
            includeDone ? (
              <button
                type="button"
                onClick={hideDone}
                data-testid="team-requests-done-toggle-on"
                className="inline-flex items-center gap-2 text-ivory px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full hover:opacity-90 transition-opacity"
                style={{ background: STATUS_COLORS.green }}
                aria-label="Erledigte ausblenden"
              >
                <Check className="w-3.5 h-3.5" strokeWidth={2} />
                Auch erledigte · {doneCount}
                <X className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
              </button>
            ) : (
              <button
                type="button"
                onClick={showDone}
                data-testid="team-requests-done-toggle-off"
                className="inline-flex items-center gap-2 text-navy px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full border border-navy/25 hover:border-green-500 transition-colors"
                aria-label={`Auch erledigte Anfragen einblenden (${doneCount})`}
              >
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: STATUS_COLORS.green }} />
                Auch erledigte ({doneCount})
              </button>
            )
          )}
          {/* Feb 2026 · Chip „Nur an Buchung geknüpft" — orthogonal zu den
              anderen Filtern. Sichtbar sobald es überhaupt verknüpfte
              Anfragen gibt (Zähler > 0) ODER der Filter aktiv ist. */}
          {(bookingLinkedOnly || bookingLinkedOpenCount > 0) && (
            bookingLinkedOnly ? (
              <button
                type="button"
                onClick={disableBookingLinkedOnly}
                data-testid="team-requests-linked-toggle-on"
                className="inline-flex items-center gap-2 text-ivory px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full hover:opacity-90 transition-opacity"
                style={{ background: BRAND_COLORS.gold }}
                aria-label={"Filter \u201eNur an Buchung geknüpft\u201c zurücksetzen"}
              >
                <CalIcon className="w-3.5 h-3.5" strokeWidth={2} />
                Nur an Buchung geknüpft · {bookingLinkedOpenCount}
                <X className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
              </button>
            ) : (
              <button
                type="button"
                onClick={enableBookingLinkedOnly}
                data-testid="team-requests-linked-toggle-off"
                className="inline-flex items-center gap-2 text-navy px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full border hover:opacity-90 transition-colors"
                style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.55), color: BRAND_COLORS.navyDark }}
                aria-label={`Nur an Buchung geknüpfte Anfragen anzeigen (${bookingLinkedOpenCount})`}
              >
                <CalIcon className="w-3.5 h-3.5" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
                Nur an Buchung geknüpft ({bookingLinkedOpenCount})
              </button>
            )
          )}
          {/* Feb 2026 · Kombinierter Hinweis-Text — informiert klar, wenn
              beide Prioritäts-Filter zusammen aktiv sind. „Hier brennt es
              für die nächste Anreise" spielt direkt auf die User-Directive
              zur Kombinierbarkeit an. */}
          {urgentOnly && bookingLinkedOnly ? (
            <span
              className="text-[11.5px] italic font-medium"
              style={{ color: URGENT_COLOR_DARK }}
              data-testid="team-requests-filter-combined-hint"
            >
              Kombiniert: dringend UND an eine Buchung geknüpft — hier brennt
              es für die nächste Anreise · {urgentAndLinkedOpenCount} offen.
            </span>
          ) : urgentOnly ? (
            <span className="text-[11.5px] text-navy-dark/60 italic">
              Gefilterte Liste — nur offene Anfragen mit Priorität „Dringend".
            </span>
          ) : bookingLinkedOnly ? (
            <span className="text-[11.5px] text-navy-dark/60 italic">
              Gefilterte Liste — nur an eine Buchung geknüpfte offene Anfragen.
            </span>
          ) : !includeDone ? (
            <span className="text-[11.5px] text-navy-dark/50 italic">
              Nur offene Anfragen · erledigte sind ausgeblendet.
            </span>
          ) : null}

          {/* Feb 2026 · 9 Sep 2026 · Saved Filters — benannte Ansichten */}
          {canCreate && (
            <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="team-requests-views">
              {savedViews.length > 0 && (
                <>
                  <span className="label-caps text-navy-dark/50">Ansichten</span>
                  {savedViews.map((v) => (
                    <div key={v.id} className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => applyView(v)}
                        data-testid={`team-requests-view-${v.id}`}
                        title={
                          `Filter: ${[
                            v.filters?.urgent ? "dringend" : null,
                            v.filters?.done ? "auch erledigte" : null,
                            v.filters?.linked ? "an Buchung geknüpft" : null,
                          ].filter(Boolean).join(" · ") || "keine"}`
                        }
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase text-navy border border-navy/25 hover:border-gold rounded-full px-3 py-1.5 transition-colors"
                      >
                        <Bookmark className="w-3.5 h-3.5" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
                        {v.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteView(v)}
                        data-testid={`team-requests-view-delete-${v.id}`}
                        title="Ansicht löschen"
                        className="text-navy-dark/40 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                      </button>
                    </div>
                  ))}
                </>
              )}
              <button
                type="button"
                onClick={() => { setSaveViewName(""); setSaveViewOpen(true); }}
                data-testid="team-requests-view-save-btn"
                title="Aktuelle Filter-Kombination als benannte Ansicht speichern"
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy border border-dashed border-navy/25 hover:border-gold rounded-full px-3 py-1.5 transition-colors"
              >
                <Save className="w-3.5 h-3.5" strokeWidth={1.8} />
                Ansicht speichern
              </button>
            </div>
          )}
        </div>
      )}

      {/* Feb 2026 · 9 Sep 2026 · Saved-View-Speichern-Dialog */}
      {saveViewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: withAlpha(BRAND_COLORS.navyDark, 0.75) }}
          onClick={() => setSaveViewOpen(false)}
          data-testid="team-requests-view-save-dialog"
        >
          <div
            role="dialog"
            aria-modal="true"
            className="bg-white w-full max-w-md relative p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSaveViewOpen(false)}
              className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
              aria-label="Schliessen"
            >
              <X className="w-4 h-4" strokeWidth={1.6} />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <Bookmark className="w-5 h-5" strokeWidth={1.4} style={{ color: BRAND_COLORS.gold }} />
              <h2 className="font-display text-2xl text-navy leading-tight">Ansicht speichern</h2>
            </div>
            <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-4">
              Aktuelle Filter:{" "}
              <span className="italic">
                {[
                  urgentOnly ? "dringend" : null,
                  includeDone ? "auch erledigte" : null,
                  bookingLinkedOnly ? "an Buchung geknüpft" : null,
                ].filter(Boolean).join(" · ") || "keine (alle offenen Anfragen)"}
              </span>
            </p>
            <label className="label-caps block mb-1.5">Name der Ansicht</label>
            <input
              autoFocus
              maxLength={60}
              value={saveViewName}
              onChange={(e) => setSaveViewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveCurrentView(); } }}
              placeholder={"z. B. \u201eMorgen brennt es\u201c"}
              data-testid="team-requests-view-name-input"
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[14px] text-navy mb-5"
            />
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setSaveViewOpen(false)}
                className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={saveCurrentView}
                disabled={savingView || !saveViewName.trim()}
                data-testid="team-requests-view-save-confirm"
                className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND_COLORS.navy }}
              >
                {savingView && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {items.length === 0 && (
          <div className="bg-white border border-dashed border-navy/20 py-16 text-center text-[13px] text-navy-dark/50" data-testid="team-requests-empty">
            {propertyFilter
              ? `Keine offenen Anfragen für ${propertyFilterName || "dieses Objekt"}.`
              : urgentOnly && bookingLinkedOnly
              ? "Aktuell keine dringenden und an eine Buchung geknüpften Anfragen — nichts brennt gerade."
              : bookingLinkedOnly
                ? "Aktuell keine offenen, an eine Buchung geknüpften Anfragen."
                : urgentOnly ? "Keine dringenden offenen Anfragen." : "—"}
          </div>
        )}
        {items.map((item) => {
          if (item.kind === "single") {
            return (
              <RequestCard key={item.request.id} r={item.request} team={team} user={user}
                onSetStatus={setStatus} onAssign={assign} onOpenDetail={setOpenDetail} t={t} />
            );
          }
          const isCollapsed = !!collapsed[item.batch_id];
          const openCount = item.requests.filter((r) => !["completed", "cancelled"].includes(r.status)).length;
          return (
            <div key={item.batch_id}
              className={`bg-ivory/50 border border-gold/40 transition-opacity ${openCount === 0 ? "opacity-70" : ""}`}
              style={openCount === 0 ? { background: withAlpha(STATUS_COLORS.green, 0.06), borderColor: withAlpha(STATUS_COLORS.green, 0.4) } : {}}
              data-testid={`team-batch-${item.batch_id}`}>
              <div className="w-full flex items-center gap-4 px-5 md:px-6 py-4 hover:bg-ivory/80 transition-colors">
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [item.batch_id]: !c[item.batch_id] }))}
                  data-testid={`team-batch-toggle-${item.batch_id}`}
                  className="flex items-center gap-4 flex-1 min-w-0 text-left"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={openCount === 0 ? { background: STATUS_COLORS.green } : { background: STATUS_COLORS.blue }}
                  >
                    {openCount === 0
                      ? <Check className="w-4 h-4" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />
                      : <Package className="w-4 h-4" style={{ color: BRAND_COLORS.ivory }} strokeWidth={1.7} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="label-caps" style={{ color: openCount === 0 ? STATUS_COLORS.green : STATUS_COLORS.blue }}>
                      {openCount === 0 ? "Sammel-Bestellung · erledigt" : "Sammel-Bestellung · in Bearbeitung"}
                    </div>
                    <div className={`font-display text-lg md:text-xl leading-tight ${openCount === 0 ? "text-navy-dark/60 line-through decoration-navy-dark/25" : "text-navy"}`}>
                      {item.requests.length} Leistung{item.requests.length === 1 ? "" : "en"}
                      {item.customer_name && <span className="text-navy-dark/70"> · {item.customer_name}</span>}
                    </div>
                    <div className="text-[11.5px] text-navy-dark/60 mt-0.5">
                      {deDateShortTime(item.first_at)}
                      {" · "}{openCount} offen · {item.requests.length - openCount} erledigt
                    </div>
                  </div>
                </button>
                {user?.role === "admin" && (
                  <button
                    type="button"
                    onClick={() => assignBatch(item.batch_id, user?.id)}
                    data-testid={`team-batch-take-${item.batch_id}`}
                    disabled={openCount === 0}
                    title={openCount === 0 ? "Alle Positionen bereits erledigt" : "Ich übernehme alle offenen Positionen"}
                    className="text-[11px] tracking-[0.15em] uppercase text-navy border border-navy/25 hover:border-gold px-3 py-2 disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                  >
                    Ich übernehme alle
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => completeBatch(item.batch_id, openCount)}
                  data-testid={`team-batch-complete-${item.batch_id}`}
                  disabled={openCount === 0}
                  title={openCount === 0 ? "Bereits alles erledigt" : "Gesamte Sammel-Bestellung als erledigt markieren"}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-ivory px-3 py-2 disabled:opacity-30 disabled:cursor-not-allowed shrink-0 hover:opacity-90"
                  style={{ background: BRAND_COLORS.navy }}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                  Alle erledigt
                </button>
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [item.batch_id]: !c[item.batch_id] }))}
                  aria-label={isCollapsed ? "Aufklappen" : "Zuklappen"}
                  className="text-navy-dark/50 shrink-0 p-1"
                >
                  {isCollapsed
                    ? <ChevronRight className="w-4 h-4" strokeWidth={1.6} />
                    : <ChevronDown className="w-4 h-4" strokeWidth={1.6} />}
                </button>
              </div>
              {!isCollapsed && (
                <div className="space-y-2 px-3 md:px-4 pb-3 md:pb-4" data-testid={`team-batch-items-${item.batch_id}`}>
                  {item.requests.map((r) => (
                    <RequestCard key={r.id} r={r} team={team} user={user}
                      onSetStatus={setStatus} onAssign={assign} onOpenDetail={setOpenDetail} t={t} indent />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Feb 2026 · User-Directive · Vollständige Detailansicht mit allen
          Kunden-Angaben — geöffnet per Klick auf Titel/Beschreibung einer
          Card. Portal-basiertes Modal, Body-Scroll-Lock, ESC-schließbar. */}
      {openDetail && (
        <RequestDetailModal
          request={openDetail}
          onClose={() => setOpenDetail(null)}
          onLinkedChange={() => { setOpenDetail(null); load(); }}
        />
      )}
    </div>
  );
}
